import React, { useId } from 'react'
import { ImageSlot } from '@/models/imageSlot'

interface TemplateImageOverlayProps {
  backgroundImage: string
  slots: ImageSlot[]
  images: string[]
  mask?: string
  className?: string
}

export default function TemplateImageOverlay({
  backgroundImage,
  slots,
  images,
  mask,
  className = ''
}: TemplateImageOverlayProps) {
  const maskId = useId()

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Background image */}
      <img
        src={backgroundImage}
        alt="Template background"
        className="w-full object-contain relative z-0"
      />

      {/* Overlay images */}
      {slots.map((slot, index) => {
        if (index >= images.length) return null

        const imageUrl = images[index]

        const style: React.CSSProperties = {
          position: 'absolute',
          top: `${slot.top}%`,
          left: `${slot.left}%`,
          width: `${slot.width}%`,
          height: `${slot.height}%`,
          overflow: 'hidden',
          transformOrigin: 'top left',
          zIndex: 5,
        }

        if (slot.transform) {
          style.transform = slot.transform
        } else if (slot.rotation) {
          style.transform = `rotate(${slot.rotation}deg)`
        }

        // If corners are provided, use a different rendering approach
        if (slot.corners) {
          const { topLeft, topRight, bottomRight, bottomLeft } = slot.corners

          // Calculate bounding box
          const minX = Math.min(topLeft[0], topRight[0], bottomRight[0], bottomLeft[0])
          const maxX = Math.max(topLeft[0], topRight[0], bottomRight[0], bottomLeft[0])
          const minY = Math.min(topLeft[1], topRight[1], bottomRight[1], bottomLeft[1])
          const maxY = Math.max(topLeft[1], topRight[1], bottomRight[1], bottomLeft[1])

          // const centerX = (minX + maxX) / 2
          // const centerY = (minY + maxY) / 2
          const width = maxX - minX
          const height = maxY - minY

          // Convert corner coordinates to percentages relative to the bounding box
          const clipPath = `polygon(
            ${(topLeft[0] - minX) / width * 100}% ${(topLeft[1] - minY) / height * 100}%,
            ${(topRight[0] - minX) / width * 100}% ${(topRight[1] - minY) / height * 100}%,
            ${(bottomRight[0] - minX) / width * 100}% ${(bottomRight[1] - minY) / height * 100}%,
            ${(bottomLeft[0] - minX) / width * 100}% ${(bottomLeft[1] - minY) / height * 100}%
          )`

          // Position and size the container to match the polygon bounds
          const containerStyle: React.CSSProperties = {
            position: 'absolute',
            left: `${minX / 10}%`,
            top: `${minY / 10}%`,
            width: `${width / 10}%`,
            height: `${height / 10}%`,
            zIndex: 5,
            clipPath,
            WebkitClipPath: clipPath,
          }

          return (
            <div key={index} style={containerStyle}>
              <img
                src={imageUrl}
                alt={`Overlay ${index + 1}`}
                className="w-full h-full object-cover"
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </div>
          )
        }

        return (
          <div key={index} style={style}>
            <img
              src={imageUrl}
              alt={`Overlay ${index + 1}`}
              className="w-full h-full object-cover"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </div>
        )
      })}

      {/* Mask overlay - renders background image again with SVG mask */}
      {mask && (
        <div className="absolute inset-0 pointer-events-none z-10">
          {(() => {
            // Always use the full image viewBox
            const imageViewBox = "0 0 1000 1000"

            // Extract just the inner content of the SVG (remove svg tags)
            const maskContent = mask.replace(/<svg[^>]*>|<\/svg>/g, '')

            return (
              <svg
                width="100%"
                height="100%"
                viewBox={imageViewBox}
                preserveAspectRatio="xMidYMid meet"
                className="absolute inset-0 w-full h-full"
              >
                <defs>
                  <mask id={`mask-${maskId}`}>
                    <g dangerouslySetInnerHTML={{ __html: maskContent }} />
                  </mask>
                </defs>
                <image
                  href={backgroundImage}
                  x="0"
                  y="0"
                  width="1000"
                  height="1000"
                  preserveAspectRatio="xMidYMid meet"
                  mask={`url(#mask-${maskId})`}
                />
              </svg>
            )
          })()}
        </div>
      )}
    </div>
  )
}
